import { UseQueryResult } from '@tanstack/react-query';
import { Alert, Card, Col, Flex, Progress, Row, Typography } from 'antd';
import { useTranslation } from 'translations/hooks';
import { getLocale } from 'translations/locale';

import { SECONDARY_MAX_DECIMAL_DIGITS } from 'business/constants';
import { useData } from 'business/dashboard/components/advancement-card/hooks/use-data';
import CardValue from 'business/dashboard/components/card-value';
import { DashboardTbmInfosV1ControllerGetTbmInfosError } from 'generated/apiComponents';
import { GetDashboardTbmInfosV1ResponseDto } from 'generated/apiSchemas';
import { GetTbmInfosQueryResult } from 'generated/graphql';
import { createNumberFormatter } from 'technical/format/number';
import Loader from 'ui/loader';

import styles from './index.module.scss';

interface AdvancementCardProps {
  tbmInfos: GetTbmInfosQueryResult;
  pmData: UseQueryResult<
    GetDashboardTbmInfosV1ResponseDto,
    DashboardTbmInfosV1ControllerGetTbmInfosError
  >;
}

export const AdvancementCard = ({ tbmInfos, pmData }: AdvancementCardProps) => {
  const { t } = useTranslation();
  const locale = getLocale();

  const extractedData = useData(tbmInfos.data, pmData.data);

  if (tbmInfos.error || pmData.error) {
    return <Alert message={t('errors.error_generic')} type="error" />;
  }

  if (tbmInfos.loading || pmData.isLoading) {
    return <Loader />;
  }

  if (!extractedData) {
    return <Alert message={t('errors.error_generic')} type="error" />;
  }

  const {
    percent,
    currentRingNumber,
    totalRings,
    currentMetricPoint,
    totalMeters,
  } = extractedData;

  const threeDecimalsNumberFormatter = createNumberFormatter(locale);
  const twoDecimalsNumberFormatter = createNumberFormatter(
    locale,
    SECONDARY_MAX_DECIMAL_DIGITS,
  );

  return (
    <Card className={styles.card}>
      <Flex vertical gap={10}>
        <Flex justify="space-between">
          <Typography>{t('dashboard.tbm.globalAdvancement.title')}</Typography>
          <Typography.Text strong>
            {twoDecimalsNumberFormatter.format(percent)}%
          </Typography.Text>
        </Flex>
        <Progress
          percent={percent}
          size={['100%', 20]}
          showInfo={false}
          className={styles.progress}
        />
        <Row gutter={[10, 10]}>
          <Col xs={24} lg={12}>
            <CardValue
              title={t('dashboard.tbm.globalAdvancement.ringNumber.title')}
              value={currentRingNumber}
              footer={
                totalRings
                  ? t('dashboard.tbm.globalAdvancement.ringNumber.total', {
                      totalRings,
                    })
                  : undefined
              }
            />
          </Col>
          <Col xs={24} lg={12}>
            <CardValue
              title={t('dashboard.tbm.globalAdvancement.metricPoint.title')}
              value={t('dashboard.tbm.globalAdvancement.metricPoint.value', {
                currentMetricPoint:
                  threeDecimalsNumberFormatter.format(currentMetricPoint),
              })}
              footer={t('dashboard.tbm.globalAdvancement.metricPoint.total', {
                totalMeters: totalMeters
                  ? threeDecimalsNumberFormatter.format(parseFloat(totalMeters))
                  : '-',
              })}
            />
          </Col>
        </Row>
      </Flex>
    </Card>
  );
};
